import { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from '@/common/components/ui/input';
import { Label } from '@/common/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/common/components/ui/select';
import type { DeviceParameterDefinition } from '@/features/devices/services/schemas';
import { buildParameterValueSchema } from '@/features/devices/utils/buildParameterValueSchema';

export type ParamSaveStatus = 'idle' | 'pending' | 'success' | 'error';

interface ParameterFieldProps {
  definition: DeviceParameterDefinition;
  value: string;
  onChange: (value: string) => void;
  status?: ParamSaveStatus;
  errorMessage?: string;
}

export function ParameterField({
  definition,
  value,
  onChange,
  status = 'idle',
  errorMessage,
}: ParameterFieldProps) {
  const [localError, setLocalError] = useState<string | null>(null);

  const label = definition.units ? `${definition.name} (${definition.units})` : definition.name;
  const inputId = `param-${definition.id}`;

  const enumOptions = definition.valueValidatorRules?.enum;

  function validate(val: string) {
    const result = buildParameterValueSchema(definition).safeParse(val);
    setLocalError(result.success ? null : (result.error.issues[0]?.message ?? null));
  }

  const displayError = errorMessage ?? localError;
  const isInvalid = status === 'error' || localError !== null;

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={inputId} className="flex items-center gap-1.5">
        <span>
          {label}
          {definition.comment && (
            <span className="ml-1.5 text-xs text-muted-foreground font-normal">
              — {definition.comment}
            </span>
          )}
        </span>
        {status === 'pending' && (
          <Loader2 size={14} className="animate-spin text-muted-foreground shrink-0" />
        )}
        {status === 'success' && <CheckCircle2 size={14} className="text-green-600 shrink-0" />}
      </Label>

      {enumOptions ? (
        <Select
          value={value}
          onValueChange={(val) => {
            onChange(val);
            setLocalError(null);
          }}
        >
          <SelectTrigger
            id={inputId}
            aria-invalid={isInvalid || undefined}
            aria-describedby={displayError ? `${inputId}-error` : undefined}
            className={isInvalid ? 'border-destructive' : undefined}
          >
            <SelectValue placeholder={definition.value ?? ''} />
          </SelectTrigger>
          <SelectContent>
            {enumOptions.map((choice) => {
              const optionValue = String(choice);
              return (
                <SelectItem key={optionValue} value={optionValue}>
                  {optionValue}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={inputId}
          type={definition.valueType === 'string' ? 'text' : 'number'}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
            if (localError !== null) validate(e.target.value);
          }}
          onBlur={() => validate(value)}
          placeholder={definition.value ?? ''}
          aria-invalid={isInvalid || undefined}
          aria-describedby={displayError ? `${inputId}-error` : undefined}
          className={isInvalid ? 'border-destructive' : undefined}
        />
      )}

      {displayError && (
        <p id={`${inputId}-error`} className="text-xs text-destructive">
          {displayError}
        </p>
      )}
    </div>
  );
}
